/**
 * Technician "Parts" tab — the tech's own parts to gather for the day.
 *
 * The "before you head out" review: defaults to the tech's focus day (today in
 * the morning, the next work day once today's jobs are done — the server
 * resolves it when no date is passed), groups parts under each job with a real
 * photo of the device, and lets the tech confirm each part — "Got it" or flag a
 * "Problem" (wrong part / missing / damaged / wrong quantity / other). Those
 * confirmations are SAVED SERVER-SIDE (parts_stage_checkoffs), which is what
 * lets the office see what's gathered, alerts them on a flag, and lets the
 * pre-dispatch gate enforce "confirm before you roll". No pricing is ever shown.
 * Source: GET /api/parts-to-stage/mine; writes POST /api/parts-to-stage/checkoff.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { useOfflineSync, queueOfflineAction } from "@/lib/offline-sync";
import { AttachmentImage } from "@/components/attachment-image";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { PageSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Boxes, Check, Flag, PackageCheck } from "lucide-react";
import { useFieldRedesign } from "@/lib/use-field-redesign";
import { focusDayNote, partsProgress } from "./redesign-screens-copy";

type Arrival = "received" | "partial" | "on_order";
type CheckoffState = "got" | "flagged" | null;
export type IssueType = "wrong_part" | "missing" | "damaged" | "wrong_quantity" | "other";

export const ISSUE_OPTIONS: { t: IssueType; label: string }[] = [
    { t: "wrong_part", label: "Wrong part ordered" },
    { t: "missing", label: "Missing / not here" },
    { t: "damaged", label: "Damaged" },
    { t: "wrong_quantity", label: "Wrong quantity" },
    { t: "other", label: "Something else" },
];

interface StagePart {
    poLineId: string;
    partName: string;
    partNumber: string | null;
    /** The device this part is for (#1458 stage 1). */
    device: { assetId: string; label: string | null; serial: string | null; location: string | null } | null;
    quantity: number;
    receivedQty: number;
    arrivalStatus: Arrival;
    checkoffState: CheckoffState;
    issueType: IssueType | null;
    issueLabel: string | null;
    checkoffNote: string | null;
}
interface StageJob {
    jobId: string;
    jobName: string;
    jobNumber: string | null;
    deviceImageId: string | null;
    parts: StagePart[];
}
interface MineResponse {
    date: string;
    timezone: string;
    jobs: StageJob[];
}

function localDateStr(offsetDays: number): string {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

function niceDate(isoDate: string): string {
    const d = new Date(`${isoDate}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** Calendar day after an ISO date, without touching the local clock. */
export function nextDay(isoDate: string): string {
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate() + 1);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

export interface DayChip {
    date: string;
    label: string;
}

/**
 * The day chips, derived from data (#798).
 *
 * They used to be two hardcoded offsets off the browser clock —
 * `localDateStr(0)` labelled "Today" and `localDateStr(1)` labelled with its
 * date — while the SERVER resolved its own focus day (`next_with_jobs`: the
 * soonest day that still has open jobs, falling back to today) and returned it
 * as `stageDate`. Nothing reconciled the two. So the tab could show "Today"
 * and "Wed, Aug 12" as separate chips on Wednesday Aug 12 — two chips, two
 * different query params, two completely different parts lists, and a tech who
 * checks one of them walks out missing half their parts.
 *
 * Now the server's `stageDate` is authoritative and always present, the local
 * day is added only when it is genuinely a different date, and the set is
 * deduped by date — so two chips can never resolve to the same day again. The
 * browser clock only ever decides *labels* ("Today"/"Tomorrow"), never which
 * days exist.
 */
export function buildDayChips(stageDate: string, todayLocal: string): DayChip[] {
    const label = (date: string): string => {
        if (date === todayLocal) return "Today";
        if (date === nextDay(todayLocal)) return `Tomorrow — ${niceDate(date)}`;
        return niceDate(date);
    };
    // Deliberate order: today (when it's a real option), the server's focus
    // day, then one day past it to look ahead. Deduped and sorted so the row
    // reads left-to-right in time.
    const dates = [todayLocal, stageDate, nextDay(stageDate)];
    const unique = Array.from(new Set(dates)).sort();
    return unique.map((date) => ({ date, label: label(date) }));
}

/**
 * #950 SPEC P4 — where each part physically IS, in plain words.
 *
 * The tab already knew this and said it in warehouse shorthand: "Ready",
 * "Partial", "On order". A green hire reads "Ready" as "ready for you to
 * install", not "it is sitting at the shop waiting to be picked up". The
 * arrival status is the only location signal the parts-to-stage payload
 * carries, so this translates it honestly rather than inventing a shelf.
 */
export function partLocationWords(arrival: Arrival, receivedQty: number, quantity: number): string {
    if (arrival === "received") return "at the shop — grab it before you roll";
    if (arrival === "partial") return `only ${receivedQty} of ${quantity} is at the shop so far`;
    return "not here yet — still on order, don't wait on it";
}

const ARRIVAL_META: Record<Arrival, { label: string; cls: string }> = {
    received: { label: "Ready", cls: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
    partial: { label: "Partial", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
    on_order: { label: "On order", cls: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
};

interface CheckoffVars {
    poLineId: string;
    state: "got" | "flagged" | "clear";
    issueType?: IssueType;
    note?: string;
}

/** Apply a check-off to the cached list so the UI reacts instantly (works
 *  offline too, before the queued write syncs). */
function applyCheckoff(data: MineResponse, v: CheckoffVars): MineResponse {
    return {
        ...data,
        jobs: data.jobs.map((j) => ({
            ...j,
            parts: j.parts.map((p) => {
                if (p.poLineId !== v.poLineId) return p;
                if (v.state === "clear") {
                    return { ...p, checkoffState: null, issueType: null, issueLabel: null, checkoffNote: null };
                }
                if (v.state === "got") {
                    return { ...p, checkoffState: "got", issueType: null, issueLabel: null, checkoffNote: null };
                }
                return {
                    ...p,
                    checkoffState: "flagged",
                    issueType: v.issueType ?? null,
                    issueLabel: ISSUE_OPTIONS.find((o) => o.t === v.issueType)?.label ?? "Problem",
                    checkoffNote: v.note ?? null,
                };
            }),
        })),
    };
}

export function StagingTab({ userId }: { userId: string | null }) {
    const { enabled: redesign } = useFieldRedesign();
    const { isOnline } = useOfflineSync();
    const qc = useQueryClient();
    // null = let the server pick the focus day (today, or next work day once
    // today's jobs are done). The Today / Next-day chips set an explicit date.
    const [dateParam, setDateParam] = useState<string | null>(null);
    const queryKey = ["field", "parts-to-stage", userId, dateParam];

    const query = useQuery({
        queryKey,
        queryFn: () => api.get<MineResponse>(`/api/parts-to-stage/mine${dateParam ? `?date=${dateParam}` : ""}`),
        enabled: !!userId,
    });

    const stageDate = query.data?.date ?? dateParam ?? localDateStr(0);

    // Confirm / flag a part. Saved server-side; optimistically updates the
    // cached list so the tap feels instant and still works offline (queued).
    const checkoff = useMutation({
        mutationFn: async (v: CheckoffVars) => {
            const body = {
                poLineId: v.poLineId,
                date: stageDate,
                state: v.state,
                issueType: v.issueType,
                note: v.note,
            };
            if (!isOnline) {
                await queueOfflineAction({ url: "/api/parts-to-stage/checkoff", method: "POST", body });
                return;
            }
            return api.post("/api/parts-to-stage/checkoff", body);
        },
        onMutate: async (v) => {
            await qc.cancelQueries({ queryKey });
            const prev = qc.getQueryData<MineResponse>(queryKey);
            if (prev) qc.setQueryData<MineResponse>(queryKey, applyCheckoff(prev, v));
            return { prev };
        },
        onError: (e, _v, ctx) => {
            if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
            // Say what the server said. This toast used to hardcode the generic
            // line and throw `ApiError.message` away, so a hard 500 (the
            // untyped-uuid INSERT, #1547) was indistinguishable from a weak-
            // signal blip — the office spent a week reading a total outage as
            // "the iPad is being flaky". Same pattern as the before-gate step.
            const reason = e instanceof ApiError ? e.message : null;
            toast.error(reason ? `Couldn't save that — ${reason}` : "Couldn't save that — try again.");
        },
        onSettled: () => {
            // Refresh every parts-to-stage view (this list AND the field-home
            // gate banner, which shares the ["field","parts-to-stage",…] prefix).
            if (isOnline) qc.invalidateQueries({ queryKey: ["field", "parts-to-stage"] });
        },
    });

    // Flag dialog state.
    const [flagFor, setFlagFor] = useState<StagePart | null>(null);
    const [issue, setIssue] = useState<IssueType | null>(null);
    const [note, setNote] = useState("");
    const openFlag = (p: StagePart) => {
        setFlagFor(p);
        setIssue(p.issueType ?? null);
        setNote(p.checkoffNote ?? "");
    };
    const submitFlag = () => {
        if (!flagFor || !issue) return;
        checkoff.mutate({
            poLineId: flagFor.poLineId,
            state: "flagged",
            issueType: issue,
            note: note.trim() || undefined,
        });
        setFlagFor(null);
    };

    const jobs = query.data?.jobs ?? [];
    // Progress counts only parts physically at the shop (on-order can't be
    // gathered); "gathered" = marked Got it.
    const { total, gotCount } = useMemo(() => {
        let t = 0;
        let g = 0;
        for (const j of jobs)
            for (const p of j.parts) {
                if (p.arrivalStatus === "on_order") continue;
                t += 1;
                if (p.checkoffState === "got") g += 1;
            }
        return { total: t, gotCount: g };
    }, [jobs]);

    const today = localDateStr(0);
    // #798 — chips come from the server's resolved focus day, never from two
    // hardcoded clock offsets, so no two chips can point at the same date.
    const chips = useMemo(() => buildDayChips(stageDate, today), [stageDate, today]);
    const chipActive = (d: string) => dateParam === d || (dateParam === null && stageDate === d);
    const focusIsNotToday = dateParam === null && stageDate !== today;

    return (
        <div className="space-y-3">
            {/* Day chips + progress */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex overflow-hidden rounded-lg border" data-testid="stage-day-chips">
                    {chips.map((chip, i) => (
                        <button
                            key={chip.date}
                            type="button"
                            onClick={() => setDateParam(chip.date)}
                            className={cn(
                                "px-3 py-1.5 text-sm font-medium",
                                i > 0 && "border-l",
                                chipActive(chip.date)
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background text-foreground/80",
                            )}
                        >
                            {chip.label}
                        </button>
                    ))}
                </div>
                {total > 0 && (
                    <div className="min-w-[140px] flex-1">
                        <div className="mb-1 flex justify-between text-xs text-foreground/70">
                            {redesign ? (
                                <span data-testid="stage-progress-line" className="font-medium text-foreground">
                                    {partsProgress(gotCount, total)}
                                </span>
                            ) : (
                                <>
                                    <span>Gathered</span>
                                    <span>
                                        <span className="font-bold tabular-nums text-foreground">{gotCount}</span> of{" "}
                                        {total}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                            <div
                                className="h-full rounded-full bg-primary transition-[width] duration-300"
                                style={{ width: `${total ? (gotCount / total) * 100 : 0}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {redesign ? (
                <p className="text-xs text-foreground/80" data-testid="stage-focus-notice">
                    {focusDayNote({ isToday: !focusIsNotToday, dayLabel: niceDate(stageDate) })}
                </p>
            ) : (
                focusIsNotToday && (
                    <p className="text-xs text-foreground/80" data-testid="stage-focus-notice">
                        Nothing left to stage for today — showing {niceDate(stageDate)}.
                    </p>
                )
            )}

            {/* This confirmation is saved and the office sees it — so say so,
                and say what "Problem" does (it alerts them right away). */}
            {total > 0 && (
                <p className="text-xs text-foreground/70" data-testid="stage-checkbox-legend">
                    Tap <b>Got it</b> as you load each part — it&apos;s saved, so the office can see what you&apos;ve
                    grabbed. Hit <b>Problem</b> if a part is wrong or missing and they&apos;ll know right away.
                </p>
            )}

            {query.isLoading && <PageSpinner />}

            {query.isSuccess && jobs.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <PackageCheck className="mx-auto mb-3 h-10 w-10 text-foreground/80" />
                        <h3 className="mb-1 text-base font-semibold">No parts to stage</h3>
                        <p className="text-sm text-foreground/80">
                            Nothing was ordered on a PO for your jobs on {niceDate(stageDate)}.
                        </p>
                    </CardContent>
                </Card>
            )}

            {jobs.map((job) => (
                <Card key={job.jobId} className="overflow-hidden">
                    {/* Device photo banner — identify the assembly at a glance */}
                    <div className="relative h-32 w-full bg-muted">
                        {job.deviceImageId ? (
                            <AttachmentImage
                                attachmentId={job.deviceImageId}
                                alt={job.jobName}
                                variant="thumb"
                                className="h-full w-full object-cover"
                                loading="lazy"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-muted-foreground/50">
                                <Boxes className="h-8 w-8" aria-hidden="true" />
                            </div>
                        )}
                    </div>
                    <CardContent className="p-3">
                        <div className="mb-1">
                            <div className="font-semibold leading-tight">{job.jobName}</div>
                            {job.jobNumber && (
                                <span className="mt-0.5 inline-block rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                                    {job.jobNumber}
                                </span>
                            )}
                        </div>
                        <div className="divide-y">
                            {job.parts.map((p) => {
                                const st = p.checkoffState;
                                const meta = ARRIVAL_META[p.arrivalStatus];
                                const onOrder = p.arrivalStatus === "on_order";
                                return (
                                    <div
                                        key={p.poLineId}
                                        className={cn(
                                            "py-2.5",
                                            st === "flagged" && "-mx-3 bg-red-50 px-3 dark:bg-red-900/15",
                                            st === "got" && "-mx-3 bg-green-50 px-3 dark:bg-green-900/10",
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="min-w-0 flex-1">
                                                <span
                                                    className={cn(
                                                        "block font-medium leading-tight",
                                                        st === "got" && "text-muted-foreground line-through",
                                                    )}
                                                >
                                                    {p.partName}
                                                </span>
                                                {p.device && (p.device.label || p.device.serial) && (
                                                    <span className="mt-0.5 block text-xs text-foreground/80">
                                                        For{" "}
                                                        <span className="font-semibold">
                                                            {p.device.label ?? "device"}
                                                        </span>
                                                        {p.device.serial ? ` · S/N ${p.device.serial}` : ""}
                                                        {p.device.location ? ` · ${p.device.location}` : ""}
                                                    </span>
                                                )}
                                                <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                                    {p.partNumber && (
                                                        <span className="rounded bg-secondary px-1.5 py-0.5 font-mono">
                                                            {p.partNumber}
                                                        </span>
                                                    )}
                                                    ×{p.quantity}
                                                    <span
                                                        className={cn(
                                                            "rounded-full px-2 py-0.5 font-semibold",
                                                            meta.cls,
                                                        )}
                                                    >
                                                        {p.arrivalStatus === "partial"
                                                            ? `${p.receivedQty} of ${p.quantity}`
                                                            : meta.label}
                                                    </span>
                                                </span>
                                                {redesign && (
                                                    <span
                                                        className="mt-0.5 block text-xs text-foreground/70"
                                                        data-testid="stage-part-location"
                                                    >
                                                        {partLocationWords(p.arrivalStatus, p.receivedQty, p.quantity)}
                                                    </span>
                                                )}
                                            </span>
                                            <div className="flex shrink-0 items-center gap-2">
                                                {!onOrder && (
                                                    <button
                                                        type="button"
                                                        aria-pressed={st === "got"}
                                                        onClick={() =>
                                                            checkoff.mutate({
                                                                poLineId: p.poLineId,
                                                                state: st === "got" ? "clear" : "got",
                                                            })
                                                        }
                                                        className={cn(
                                                            "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold",
                                                            st === "got"
                                                                ? "border-green-600 bg-green-700 text-white"
                                                                : "border-border bg-background text-foreground/80",
                                                        )}
                                                    >
                                                        <Check className="h-3.5 w-3.5" strokeWidth={3} /> Got it
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    aria-pressed={st === "flagged"}
                                                    onClick={() => openFlag(p)}
                                                    className={cn(
                                                        "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold",
                                                        st === "flagged"
                                                            ? "border-red-600 bg-red-600 text-white"
                                                            : "border-border bg-background text-foreground/80",
                                                    )}
                                                >
                                                    <Flag className="h-3.5 w-3.5" /> {onOrder ? "Flag" : "Problem"}
                                                </button>
                                            </div>
                                        </div>
                                        {st === "flagged" && (
                                            <div className="mt-1.5 text-xs font-medium text-red-700 dark:text-red-300">
                                                🚩 {p.issueLabel}
                                                {p.checkoffNote ? ` — “${p.checkoffNote}”` : ""} · office notified
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            ))}

            {/* Flag-a-problem dialog */}
            <Dialog
                open={flagFor !== null}
                onClose={() => setFlagFor(null)}
                title={flagFor ? `What's wrong with the ${flagFor.partName}?` : "Flag a part"}
            >
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        {ISSUE_OPTIONS.map((o) => (
                            <button
                                key={o.t}
                                type="button"
                                onClick={() => setIssue(o.t)}
                                className={cn(
                                    "rounded-lg border px-3 py-2.5 text-left text-sm font-medium",
                                    o.t === "other" && "col-span-2",
                                    issue === o.t
                                        ? "border-red-600 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200"
                                        : "border-border bg-background",
                                )}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Tell the office what's up (optional)…"
                        className="min-h-[64px] w-full rounded-lg border border-border bg-background p-2.5 text-sm"
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setFlagFor(null)}
                            className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground/80"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!issue}
                            onClick={submitFlag}
                            className="flex-1 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                        >
                            Send to office
                        </button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
